import { React, useEffect, useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Typography,
  Space,
  Badge,
  Tooltip,
  Popconfirm,
  Button,
  InputNumber,
  message,
  Spin,
} from "antd";
import {
  InfoCircleOutlined,
  ShoppingCartOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const AsignacionesModal = ({
  visible,
  onClose,
  asignaciones = [],
  onDelete,
  onUpdate,
  onFetchDisponible, // Nueva prop: función para consultar el disponible actual
}) => {
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [allPOProducts, setAllPOProducts] = useState([]);
  const [loadingDisponible, setLoadingDisponible] = useState(false);
  const [disponibleActual, setDisponibleActual] = useState(null);

  useEffect(() => {
    const dataAllPOProducts = getAllPOProducts();
    setAllPOProducts(dataAllPOProducts);
    setEditingKey(null);
    setEditingValue(null);
  }, [asignaciones]);

  // Función para formatear moneda
  const formatCurrency = (valor) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(valor);
  };

  // Función para obtener solo el producto de PO que coincide con poRow.oidPo_Producto
  const getAllPOProducts = () => {
    const products = [];

    asignaciones.forEach((asignacion) => {
      const { asignacion: data } = asignacion;
      const { poRow } = data;

      // Buscar en los poRows el que coincida con poRow.oidPo_Producto
      const productoEncontrado = data.plan.poRows.find(
        (po) => po.oidPo_Producto === poRow.oidPo_Producto
      );

      if (productoEncontrado) {
        products.push({
          ...productoEncontrado,
          asignacion_id: asignacion.id,
          usuario: data.usuario,
          plan_codigo: data.plan.codigoInterno,
          modo: data.mode,
          fecha: data.fecha,
          cantidad_disponible: asignacion.cantidad_disponible,
          key: `${asignacion.id}-${productoEncontrado.oidPo_Producto}`,
          asignacion: asignacion.asignacion,
          oidPlanItem: asignacion.oidPlanItem,
          cantidad_asignada: asignacion.cantidad_asignada,
        });
      }
    });
    return products;
  };

  // Iniciar edición y consultar disponible actual
  const handleEdit = async (record) => {
    setLoadingDisponible(true);
    setEditingKey(record.key);
    setEditingValue(record.cantidad_asignada);

    try {
      // Consultar el servicio para obtener el disponible actual
      // Puedes pasar el ID de la asignación, oidPlanItem, o cualquier dato necesario
      const disponible = await onFetchDisponible(record.oidStockLote);

      setDisponibleActual(disponible);
      message.success("Disponible actualizado correctamente");
    } catch (error) {
      message.error("Error al obtener la cantidad disponible actual");
      console.error(error);
      // En caso de error, mantener el valor que se tenía
      setDisponibleActual(record.cantidad_disponible);
    } finally {
      setLoadingDisponible(false);
    }
  };

  // Cancelar edición
  const handleCancel = () => {
    setEditingKey(null);
    setEditingValue(null);
    setDisponibleActual(null);
  };

  // Guardar cambios
  const handleSave = async (record) => {
    // Usar el disponible actual consultado del servicio
    const cantidadDisponible =
      parseInt(disponibleActual ?? record.cantidad_disponible) || 0;
    const cantidadAsignadaActual = record.cantidad_asignada || 0;
    const maximoPermitido = cantidadAsignadaActual + cantidadDisponible;
    const nuevaCantidad = editingValue;

    // Validaciones
    if (nuevaCantidad === null || nuevaCantidad === undefined) {
      message.error("Debe ingresar una cantidad válida");
      return;
    }

    if (nuevaCantidad <= 0) {
      message.error("La cantidad debe ser mayor a cero");
      return;
    }

    if (nuevaCantidad > maximoPermitido) {
      message.error(
        `La cantidad no puede exceder ${maximoPermitido} (asignado: ${cantidadAsignadaActual} + disponible: ${cantidadDisponible})`
      );
      return;
    }

    // Si todo es válido, llamar al onUpdate del padre
    try {
      await onUpdate(nuevaCantidad, record);
      setEditingKey(null);
      setEditingValue(null);
      setDisponibleActual(null);
    } catch (error) {
      message.error("Error al actualizar la cantidad");
      console.error(error);
    }
  };

  const isEditing = (record) => record.key === editingKey;

  // Columnas para la tabla consolidada de productos PO
  const consolidatedPOColumns = [
    {
      title: "Acciones",
      key: "acciones",
      width: 200,
      fixed: "right",
      render: (_, record) => {
        const editing = isEditing(record);

        return editing ? (
          <Space size="small" wrap>
            <Tooltip title="Guardar cambios">
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="small"
                shape="circle"
                onClick={() => handleSave(record)}
                disabled={loadingDisponible}
              />
            </Tooltip>
            <Tooltip title="Cancelar">
              <Button
                icon={<CloseOutlined />}
                size="small"
                shape="circle"
                onClick={handleCancel}
                disabled={loadingDisponible}
              />
            </Tooltip>
          </Space>
        ) : (
          <Space size="small" wrap>
            <Tooltip title="Actualizar asignación">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                shape="circle"
                onClick={() => handleEdit(record)}
                disabled={editingKey !== null}
              />
            </Tooltip>

            <Popconfirm
              title="¿Eliminar esta asignación?"
              onConfirm={() => onDelete(record.asignacion_id)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Eliminar asignación">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  shape="circle"
                  disabled={editingKey !== null}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
    {
      title: "Asignación",
      dataIndex: "asignacion_id",
      key: "asignacion_id",
      width: 100,
      fixed: "left",
      render: (_, record) => (
        <Tag color={record.modo === "1:1" ? "green" : "orange"}>
          {record.modo}
        </Tag>
      ),
    },

    {
      title: "Plan",
      dataIndex: "plan_codigo",
      key: "plan_codigo",
      width: 100,
      render: (codigo) => <Tag color="purple">{codigo}</Tag>,
    },
    {
      title: "N° Orden",
      dataIndex: "numero_orden",
      key: "numero_orden",
      width: 100,
      align: "center"
    },
    {
      title: "Producto PO",
      dataIndex: "productoPO",
      key: "productoPO",
      width: 200,
      ellipsis: true,
    },
    // 👇 La columna Disponible se añade SOLO si hay fila en edición
    ...(editingKey
      ? [
          {
            title: "Disponible",
            dataIndex: "cantidad_disponible",
            key: "cantidad_disponible",
            width: 120,
            align: "right",
            render: (cantidad, record) => {
              if (!isEditing(record)) return null;

              const disponibleAMostrar =
                disponibleActual !== null ? disponibleActual : cantidad;

              return (
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  {loadingDisponible && editingKey === record.asignacion_id ? (
                    <Spin size="small" />
                  ) : (
                    <Tag color="#faad14">{disponibleAMostrar}</Tag>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
    {
      title: "Asignado",
      dataIndex: "cantidad_asignada",
      key: "cantidad_asignada",
      width: 100,
      align: "center",
      render: (cantidad, record) => {
        const editing = isEditing(record);
        const disponibleParaCalculo =
          disponibleActual !== null
            ? disponibleActual
            : record.cantidad_disponible;

        const maximoPermitido = cantidad + parseInt(disponibleParaCalculo || 0);

        return editing ? (
          <InputNumber
            min={1}
            max={maximoPermitido}
            value={editingValue}
            onChange={(value) => setEditingValue(value)}
            size="small"
            style={{ width: "100%" }}
            disabled={loadingDisponible}
            status={
              editingValue > 0 && editingValue <= maximoPermitido ? "" : "error"
            }
          />
        ) : (
          <Tag color="green">{cantidad}</Tag>
        );
      },
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor_nombre",
      key: "proveedor_nombre",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Unidad",
      dataIndex: "unidad_medida",
      key: "unidad_medida",
      width: 80,
      align: "center",
    },
    {
      title: "Valor Unitario",
      dataIndex: "valor_unitario",
      key: "valor_unitario",
      width: 120,
      align: "right",
      render: (valor) => formatCurrency(valor),
    },
    {
      title: "Valor Total",
      key: "valor_total",
      width: 120,
      align: "right",
      render: (_, record) => {
        const editing = isEditing(record);
        const cantidad = editing ? editingValue : record.cantidad_asignada;
        const valorTotal = record.valor_unitario * (cantidad || 0);

        return (
          <Text strong className="text-green-600">
            {formatCurrency(valorTotal)}
          </Text>
        );
      },
    },
  ];

  // Calcular totales
  const totalAsignado = allPOProducts.reduce(
    (sum, product) => sum + (product.cantidad_asignada || 0),
    0
  );

  const totalValor = allPOProducts.reduce(
    (sum, product) => sum + product.valor_unitario * product.cantidad_asignada,
    0
  );

  return (
    <Modal
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Productos de Órdenes de Compra</span>
          <Badge count={asignaciones.length} showZero />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "75vh",
          overflow: "auto",
          padding: "16px",
        },
      }}
    >
      {asignaciones.length === 0 ? (
        <div className="text-center py-8">
          <Text type="secondary">No hay asignaciones para mostrar</Text>
        </div>
      ) : (
        <div>
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <Space size="large">
              <div>
                <Text type="secondary">Total Productos:</Text>
                <Text strong className="ml-2">
                  {allPOProducts.length}
                </Text>
              </div>
              <div>
                <Text type="secondary">Cantidad Total Asignada:</Text>
                <Text strong className="ml-2">
                  {totalAsignado}
                </Text>
              </div>
              <div>
                <Text type="secondary">Valor Total:</Text>
                <Text strong className="ml-2 text-green-600">
                  {formatCurrency(totalValor)}
                </Text>
              </div>
            </Space>
          </div>

          <Table
            dataSource={allPOProducts}
            columns={consolidatedPOColumns}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
            }}
            size="small"
            scroll={{ x: 1400, y: 500 }}
          />
        </div>
      )}
    </Modal>
  );
};

export default AsignacionesModal;
